import api from './api';

// API Response interfaces based on documentation
export interface TotalCases {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface TotalRevenue {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface ActiveClients {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface SuccessRate {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface Period {
  from: string;
  to: string;
  label: string;
}

export interface ReportsSummaryData {
  total_cases: TotalCases;
  active_cases: number;
  completed_cases: number;
  total_revenue: TotalRevenue;
  active_clients: ActiveClients;
  success_rate: SuccessRate;
  period: Period;
}

export interface CasesByTypeData {
  type: string;
  cases: number;
  distribution: number;
  revenue: number;
}

export interface LawyerPerformanceData {
  name: string;
  user_id: string;
  cases_count: number;
  completed_cases: number;
  total_time: number;
  avg_satisfaction: number;
  revenue: number;
}

export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

// Legacy interfaces for backward compatibility
export interface ReportsSummary {
  total_cases: number;
  total_cases_change: number;
  total_revenue: number;
  total_revenue_change: number;
  active_clients: number;
  active_clients_change: number;
  success_rate: number;
  success_rate_change: number;
}

export interface CasesByType {
  type: string;
  count: number;
  percentage: number;
  revenue: number;
}

export interface LawyerPerformance {
  user_id: number;
  name: string;
  total_cases: number;
  completed_cases: number;
  revenue: number;
  satisfaction_rating: number;
}

export type ReportPeriod = 'this_month' | 'this_quarter' | 'this_year' | 'last_year' | 'custom';

export interface CustomDateRange {
  from: string;
  to: string;
}

const reportsService = {
  getSummary: async (userId: string, period: ReportPeriod, customDateRange?: CustomDateRange): Promise<ApiResponse<ReportsSummaryData>> => {
    let url = `/reports/summary/${userId}?period=${period}`;
    if (period === 'custom' && customDateRange) {
      url += `&from=${customDateRange.from}&to=${customDateRange.to}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  getCasesByType: async (userId: string, period: ReportPeriod, customDateRange?: CustomDateRange): Promise<ApiResponse<CasesByTypeData[]>> => {
    let url = `/reports/cases-by-type/${userId}?period=${period}`;
    if (period === 'custom' && customDateRange) {
      url += `&from=${customDateRange.from}&to=${customDateRange.to}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  getLawyerPerformance: async (userId: string, period: ReportPeriod, customDateRange?: CustomDateRange): Promise<ApiResponse<LawyerPerformanceData[]>> => {
    let url = `/reports/lawyer-performance/${userId}?period=${period}`;
    if (period === 'custom' && customDateRange) {
      url += `&from=${customDateRange.from}&to=${customDateRange.to}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  exportPdf: async (userId: string, period: ReportPeriod, customDateRange?: CustomDateRange): Promise<Blob> => {
    let url = `/reports/export/pdf/${userId}?period=${period}`;
    if (period === 'custom' && customDateRange) {
      url += `&from=${customDateRange.from}&to=${customDateRange.to}`;
    }
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  },

  exportExcel: async (userId: string, period: ReportPeriod, customDateRange?: CustomDateRange): Promise<Blob> => {
    let url = `/reports/export/excel/${userId}?period=${period}`;
    if (period === 'custom' && customDateRange) {
      url += `&from=${customDateRange.from}&to=${customDateRange.to}`;
    }
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  },
};

export default reportsService;
